import { useAppSelector, useAppDispatch } from "../services/hooks";
import React, { useEffect, useState, useCallback, Fragment } from "react";
import type { ChangeEvent, FormEvent } from "react";

import { updatePassword, getProfile } from "../services/userServices/authActions";
import NotificationAlertOptions from "./Alerts";
import PasswordModalView from "./PasswordModalView";
import { GLOBAL_NOTIFICATION_CONTAINER_ID } from "./GlobalNotifications";

// Toasts go to the shell hub's container, which outlives this component. A default
// <ToastContainer /> here was unmounted with the mandatory modal (App.jsx) and took the
// shared default registry entry with it, dropping this success toast and every later
// default toast of the open view (SCRUM-1824).
const toHub = { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID };

type PasswordModalContainerProps = {
  mandatory?: boolean;
};

const PasswordModalContainer = ({ mandatory = false }: PasswordModalContainerProps) => {
  const dispatch = useAppDispatch();
  const profile = useAppSelector((s) => s.auth?.profile) as any;
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [defaultModal, setDefaultModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!mandatory) {
      dispatch(getProfile());
    }
  }, [dispatch, mandatory]);

  const toggleModal = useCallback(() => {
    setDefaultModal((v) => !v);
  }, []);

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "currentPassword") setCurrentPassword(value);
    if (name === "password") setPassword(value);
    if (name === "confirmPassword") setConfirmPassword(value);
  }, []);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (password !== confirmPassword) {
        NotificationAlertOptions("error", "Retype Password", toHub);
        setPassword("");
        setConfirmPassword("");
        return;
      }

      if (!password) {
        NotificationAlertOptions("error", "Password is required", toHub);
        return;
      }

      setSubmitting(true);
      try {
        // SCRUM-2011: the server checks the current password before it replaces it.
        const user = { ...(profile || {}), currentPassword, password };
        await dispatch(updatePassword(user));
        NotificationAlertOptions("success", "Your password has been updated", toHub);
        setCurrentPassword("");
        setPassword("");
        setConfirmPassword("");
        if (!mandatory) {
          toggleModal();
        }
      } catch {
        NotificationAlertOptions("error", "Could not update password", toHub);
      } finally {
        setSubmitting(false);
      }
    },
    [currentPassword, password, confirmPassword, profile, dispatch, toggleModal, mandatory]
  );

  return (
    <Fragment>
      <PasswordModalView
        defaultModal={defaultModal}
        toggleModal={toggleModal}
        currentPassword={currentPassword}
        password={password}
        confirmPassword={confirmPassword}
        onChange={onChange}
        onSubmit={onSubmit}
        mandatory={mandatory}
        submitting={submitting}
      />
    </Fragment>
  );
};

export default PasswordModalContainer;
